import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useUI } from '@/hooks/ui/useUI';
import { useUnsavedChangesWarning } from '@/hooks/ui/navigation';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { workingDayService } from '@/api/domains/workingDay';
import type { WorkingDayItem } from '@/api/domains/workingDay';
import { workingDayTypeCatalogService, WORKING_DAY_TYPE_ID } from '@/api/domains/catalog/workingDayType';
import { progressivityService } from '@/api/domains/attendance';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import {
  createShiftDescriptionSchema,
  STATIC_WORKING_DAY_TYPES,
  type CreateShiftDescriptionForm,
  type CreateShiftDescriptionFormSubset,
} from '@/schemas/shift/createShiftDescriptionForm';


const WORKING_DAY_CATALOG_PAGE_SIZE = 1000;
const PARTIAL_WORKING_DAY_ORDINARY_HOURS = 30;
const DAYS_PER_WEEK = 7;
const FIXED_WEEKS_WORKING_DAY_TYPES: number[] = [
  WORKING_DAY_TYPE_ID.FIXED,
  WORKING_DAY_TYPE_ID.PARTIAL,
];

const DEFAULT_VALUES: DefaultValues<CreateShiftDescriptionForm> = {
  workingDayTypeId: null,
  workingDayId: null,
  shiftCode: '',
  shiftName: '',
  weeksCount: 1,
  isExceptionalWorkingDay: false,
  dtAuthorizationNumber: '',
  dtAuthorizationDate: '',
  staticStartDate: '',
};

export interface UseShiftCreateResult {
  formSubset: CreateShiftDescriptionFormSubset;
  isLoadingCatalogs: boolean;
  isDirty: boolean;
  workingDayTypeOptions: SelectorOption[];
  workingDayOptions: SelectorOption[];
  selectedWorkingDayTypeId: number | null;
  selectedWorkingDay: WorkingDayItem | undefined;
  isDistributedCyclesType: boolean;
  isExceptionalType: boolean;
  isStaticType: boolean;
  ordinaryHours: number | null;
  weeksCountEditable: boolean;
  isGridVisible: boolean;
  onGenerate: () => void;
  handleCancel: () => void;
  confirmNavigation: () => void;
}

export function useShiftCreate(): UseShiftCreateResult {
  const { t: translate } = useTranslation('schedule');
  const { showNotification } = useUI();
  const navigate = useNavigate();
  const [isGridVisible, setIsGridVisible] = useState(false);

  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors, isDirty },
  } = useForm<CreateShiftDescriptionForm>({
    resolver: zodResolver(createShiftDescriptionSchema),
    mode: 'onBlur',
    defaultValues: DEFAULT_VALUES,
  });

  const formSubset: CreateShiftDescriptionFormSubset = {
    register, setValue, watch, errors, trigger,
  };

  // ── Catálogos: mismos endpoints que la pestaña "Turnos" (ASIS-5049) ───────
  const {
    options: workingDayTypeOptions,
    isLoaded: isTypesLoaded,
  } = useCatalogLoader(
    (signal) => workingDayTypeCatalogService.getWorkingDayTypes(signal),
    (item) => ({ value: String(item.id), label: item.description }),
    [],
    false,
    () => showNotification('error', translate('shift.create.messages.loadCatalogsError')),
  );

  const { data: workingDays, isLoaded: isWorkingDaysLoaded } = useCatalogLoader(
    async (signal) => {
      const response = await workingDayService.list(
        { pageSize: WORKING_DAY_CATALOG_PAGE_SIZE },
        signal,
      );
      if (!response.success || !response.data) return { ...response, data: null };
      return { ...response, data: response.data.items };
    },
    (item: WorkingDayItem) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showNotification('error', translate('shift.create.messages.loadCatalogsError')),
  );

  const { data: progressivities, isLoaded: isProgressivitiesLoaded } = useCatalogLoader(
    () => progressivityService.getProgressivities(),
    (item) => ({ value: item.startDate, label: String(item.weeklyHours) }),
    [],
    false,
    () => showNotification('error', translate('shift.create.messages.loadCatalogsError')),
  );

  const isLoadingCatalogs = !isTypesLoaded || !isWorkingDaysLoaded || !isProgressivitiesLoaded;

  const selectedWorkingDayTypeId = watch('workingDayTypeId');
  const selectedWorkingDayId = watch('workingDayId');

  const isDistributedCyclesType =
    selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.DISTRIBUTED_CYCLES;
  const isExceptionalType = selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.EXCEPTIONAL;
  const isStaticType =
    selectedWorkingDayTypeId !== null &&
    STATIC_WORKING_DAY_TYPES.includes(selectedWorkingDayTypeId);
  const weeksCountEditable =
    selectedWorkingDayTypeId === null ||
    !FIXED_WEEKS_WORKING_DAY_TYPES.includes(selectedWorkingDayTypeId);

  // Jornada fija/Parcial siempre usan 1 semana — si el valor quedó en más de 1 por una
  // selección de tipo anterior (ej. "Por turno" con 2 semanas), se resetea al bloquear el campo.
  const weeksCountValue = watch('weeksCount');
  useEffect(() => {
    if (!weeksCountEditable && weeksCountValue !== 1) {
      setValue('weeksCount', 1, { shouldDirty: true, shouldValidate: true });
    }
  }, [weeksCountEditable, weeksCountValue, setValue]);

  const workingDayOptions = useMemo<SelectorOption[]>(() => {
    const enabledWorkingDays = workingDays.filter((workingDay) => workingDay.status === 'enabled');
    const filtered = selectedWorkingDayTypeId !== null
      ? enabledWorkingDays.filter(
        (workingDay) => workingDay.workingDayTypeId === selectedWorkingDayTypeId,
      )
      : enabledWorkingDays;
    return filtered.map((workingDay) => ({ value: String(workingDay.id), label: workingDay.name }));
  }, [workingDays, selectedWorkingDayTypeId]);

  const selectedWorkingDay = useMemo(
    () => workingDays.find((workingDay) => workingDay.id === selectedWorkingDayId),
    [workingDays, selectedWorkingDayId],
  );

  const currentWeeklyHours = useMemo(() => {
    const today = new Date().getTime();
    const applicable = progressivities
      .filter((progressivity) => new Date(progressivity.rawStartDate).getTime() <= today)
      .sort((left, right) =>
        new Date(right.rawStartDate).getTime() - new Date(left.rawStartDate).getTime(),
      );
    return applicable[0]?.weeklyHours ?? null;
  }, [progressivities]);

  const isFixedOrByShiftType =
    selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.FIXED ||
    selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.BY_SHIFT;

  const ordinaryHours = useMemo(() => {
    if (isFixedOrByShiftType) {
      return currentWeeklyHours;
    }
    if (selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.PARTIAL) {
      return PARTIAL_WORKING_DAY_ORDINARY_HOURS;
    }
    if (selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.BIWEEKLY) {
      // Techo dinámico (confirmado por negocio 2026-07-17, ver NE_ShiftLegalRulesEvaluator.
      // EvaluateBiweeklyLimit en el backend): días totales del ciclo (trabajo + descanso)
      // ÷ 7 × progresividad vigente. No se resta ningún día de descanso adicional.
      const workingDaysPattern = selectedWorkingDay?.workingDays;
      const offDaysPattern = selectedWorkingDay?.offDays;
      if (!workingDaysPattern || !offDaysPattern || currentWeeklyHours === null) {
        return null;
      }
      const totalCycleDays = workingDaysPattern + offDaysPattern;
      return (totalCycleDays / DAYS_PER_WEEK) * currentWeeklyHours;
    }
    if (isDistributedCyclesType || isExceptionalType) {
      return selectedWorkingDay?.ordinaryWeeklyHours ?? null;
    }
    return null;
  }, [
    isFixedOrByShiftType,
    selectedWorkingDayTypeId,
    currentWeeklyHours,
    isDistributedCyclesType,
    isExceptionalType,
    selectedWorkingDay,
  ]);

  // ── Navegación ─────────────────────────────────────────────────────────
  const navigateToList = useCallback(() => {
    void navigate(ROUTE_DEFINITIONS.TURNOS_ROTATIVOS_FIJOS.path);
  }, [navigate]);

  const { handleCancelWithWarning: handleCancel, confirmNavigation } = useUnsavedChangesWarning({
    onConfirmLeave: navigateToList,
    isDirty,
  });

  // ── Generar: valida Descripción y revela el contenedor de la grilla (ASIS-5051) ──
  const onGenerate = useCallback(() => {
    void trigger().then((isValid) => {
      if (isValid) {
        setIsGridVisible(true);
      } else {
        showNotification('error', translate('shift.create.messages.validationError'));
      }
    });
  }, [trigger, showNotification, translate]);

  return {
    formSubset,
    isLoadingCatalogs,
    isDirty,
    workingDayTypeOptions,
    workingDayOptions,
    selectedWorkingDayTypeId,
    selectedWorkingDay,
    isDistributedCyclesType,
    isExceptionalType,
    isStaticType,
    ordinaryHours,
    weeksCountEditable,
    isGridVisible,
    onGenerate,
    handleCancel,
    confirmNavigation,
  };
}
